// RigSentinel Context Engine — Phase RCE-1
//
// DERIVERS
// --------
// Pure functions that turn raw module state (Project / Rig / Well / BHA /
// Drill String / Mud) into a single, classifier-tagged value for a known
// field path. Each deriver returns:
//   { value, confidence, source }
// or `null` if the deriver has nothing to publish.
//
// Derivers NEVER write storage. They are read-only projections.
//
// Phase RCE-1 ships a foundational set covering the fields surfaced in the
// Active Operation pill (project / rig / well / hole section / depth /
// current operation / BHA / drill string / mud weight). Later phases will
// register more derivers as additional modules are migrated.

import { getProject } from "./project";
import { getWellProfile } from "@/lib/well/storage";
import { getRigSheet }    from "@/lib/rigsheet/storage";
import { getBha }         from "@/lib/bha/storage";
import { getDrillString } from "@/lib/drillstring/storage";
import { deriveMudField } from "@/lib/mud/derivers";
import { buildWellState } from "@/lib/openg/wellState";
import { getEngineeringDefaults } from "@/lib/storage";
import { toImperialValue } from "@/lib/metricUnits";
import { resolveOpenHoleDiameterIn } from "@/lib/well/casingGeometry";

// ---------------------------------------------------------------------------
// Source tag — small object that pinpoints WHERE a value came from. The
// resolver carries this through to its API and the Context Inspector renders
// it verbatim.
// ---------------------------------------------------------------------------
function src(module, id, label) {
  return { module, id: id || "", label: label || "" };
}

const _isNonEmpty = (v) => v !== "" && v !== null && v !== undefined;

// Support correction — Aug 2026, Phase 2. Classification/Status ship with
// sane non-empty schema defaults (wellType, wellPurpose, environment,
// bopLocation, status="planned") so a plain non-empty check would publish
// them as "exact" confidence before the operator ever confirms that
// section. `sectionsTouched` (well/schema.js) already records whether a
// section was explicitly saved via WellProfileEditor.jsx's own save
// gating — the deriver now consults the SAME flag before publishing these
// fields. When the owning section was never touched, the deriver
// publishes nothing (no candidate), so resolve() falls through to its
// existing "unknown" confidence — no new confidence value introduced.
function _sectionConfirmed(w, sectionKey) {
  return Boolean(w?.sectionsTouched && w.sectionsTouched[sectionKey]);
}

// ---------------------------------------------------------------------------
// RC-1 — Effective BHA / Drill String resolution.
//
// Precedence (highest first):
//   1. Engineering Session override — `ctx.bhaId` / `ctx.drillStringId`
//      (set by SessionSwitcher for what-if analysis).
//   2. Well's canonical Active Configuration — `well.activeBhaId` /
//      `well.activeDrillStringId` (assigned automatically on first
//      BHA / Drill String creation, or via `setActive*ForWell`).
//   3. "" — caller sees no value, no crash.
//
// These helpers are intentionally private and live in this file so they
// stay in lock-step with the deriver pipeline. They never throw.
// ---------------------------------------------------------------------------
function _effectiveBhaId(ctx) {
  if (ctx && _isNonEmpty(ctx.bhaId)) return ctx.bhaId;
  if (!ctx || !ctx.wellId) return "";
  try {
    const w = getWellProfile(ctx.wellId);
    return (w && _isNonEmpty(w.activeBhaId)) ? w.activeBhaId : "";
  } catch { return ""; }
}

function _effectiveDrillStringId(ctx) {
  if (ctx && _isNonEmpty(ctx.drillStringId)) return ctx.drillStringId;
  if (!ctx || !ctx.wellId) return "";
  try {
    const w = getWellProfile(ctx.wellId);
    return (w && _isNonEmpty(w.activeDrillStringId)) ? w.activeDrillStringId : "";
  } catch { return ""; }
}

// ---------------------------------------------------------------------------
// Project profile derivers
// ---------------------------------------------------------------------------
function _deriveFromProject(field, ctx) {
  if (!ctx.projectId) return null;
  const p = getProject(ctx.projectId);
  if (!p) return null;
  const tag = src("project", p.id, p.name);
  switch (field) {
    case "project.id":        return { value: p.id,        confidence: "exact",     source: tag };
    case "project.name":      return _isNonEmpty(p.name)       ? { value: p.name,       confidence: "exact", source: tag } : null;
    case "project.operator":  return _isNonEmpty(p.operator)   ? { value: p.operator,   confidence: "exact", source: tag } : null;
    case "project.client":    return _isNonEmpty(p.client)     ? { value: p.client,     confidence: "exact", source: tag } : null;
    case "project.country":   return _isNonEmpty(p.country)    ? { value: p.country,    confidence: "exact", source: tag } : null;
    case "project.field":     return _isNonEmpty(p.field)      ? { value: p.field,      confidence: "exact", source: tag } : null;
    case "project.block":     return _isNonEmpty(p.block)      ? { value: p.block,      confidence: "exact", source: tag } : null;
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Rig Sheet derivers
// ---------------------------------------------------------------------------
function _deriveFromRig(field, ctx) {
  if (!ctx.rigId) return null;
  const rs = getRigSheet(ctx.rigId);
  if (!rs) return null;
  const p = rs.payload || {};
  const tag = src("rig", rs.id, p.rigName);
  switch (field) {
    case "rig.id":         return { value: rs.id,           confidence: "exact",     source: tag };
    case "rig.name":       return _isNonEmpty(p.rigName)       ? { value: p.rigName,       confidence: "exact", source: tag } : null;
    case "rig.type":       return _isNonEmpty(p.rigType)       ? { value: p.rigType,       confidence: "exact", source: tag } : null;
    case "rig.contractor": return _isNonEmpty(p.rigContractor) ? { value: p.rigContractor, confidence: "exact", source: tag } : null;
    // Five-Defect Correction Pass, Defect 2 — the Rig Data Sheet already
    // stores its own operator/country (denormalized identity fields), but
    // no deriver ever exposed them, so Well Profile prefill could only see
    // them via a Project Profile (which most sessions never create). Exact
    // 1:1 field-name match, same pattern as rig.name/rig.contractor above.
    case "rig.operator":   return _isNonEmpty(p.operator)      ? { value: p.operator,      confidence: "exact", source: tag } : null;
    case "rig.country":    return _isNonEmpty(p.country)       ? { value: p.country,       confidence: "exact", source: tag } : null;
    // Field/Block are now first-class separate fields on the Rig Data Sheet
    // (schema.js), so they publish as an exact 1:1 match like the identity
    // fields above. The legacy combined `fieldOrBlock` string is deliberately
    // NOT consulted here — splitting it would be free-text guessing.
    case "rig.field":      return _isNonEmpty(p.field)         ? { value: p.field,         confidence: "exact", source: tag } : null;
    case "rig.block":      return _isNonEmpty(p.block)         ? { value: p.block,         confidence: "exact", source: tag } : null;
    case "rig.pumpOutputBblStk": {
      // Pump selection order: designated KILL pump → designated circulating
      // pump → first pump with a non-blank computed.dispBblPerStk.
      //
      // The kill pump leads because every consumer of this field is a
      // well-control context — the Kill Sheet (killsheet/consumes.js) and
      // nine kill/choke/riser stroke calculators in catalog.js — where the
      // pump that will actually circulate the kill is the correct one.
      // Previously only defaultCirculatingPumpId was read, so designating
      // Pump 3 as the primary kill pump had no effect and the kill sheet
      // silently inherited Pump 1's output.
      const pumps = Array.isArray(p.pumps) ? p.pumps : [];
      if (pumps.length === 0) return null;
      const killId = p.defaults?.defaultKillPumpId || "";
      const circId = p.defaults?.defaultCirculatingPumpId || "";
      const pickedDefault =
        (killId && pumps.find((x) => x.id === killId))
        || (circId && pumps.find((x) => x.id === circId))
        || null;
      const candidate = pickedDefault
        ? pickedDefault
        : pumps.find((x) => _isNonEmpty(x.computed?.dispBblPerStk));
      const disp = candidate?.computed?.dispBblPerStk;
      if (!_isNonEmpty(disp)) return null;
      return { value: disp, confidence: "derived",
        source: { ...tag, label: `${candidate.label || "Pump"} · ${candidate.pumpType || ""}`.trim() } };
    }
    case "rig.surfaceLineVolBbl": {
      const vol = p.wellControlVolumes?.surfaceLineVolume;
      if (!_isNonEmpty(vol)) return null;
      const n = Number(vol);
      if (!Number.isFinite(n) || n < 0) return null;
      // Card 4 stores the value in the RIG SHEET's own unit system. This
      // path is published as BARRELS, so a metric rig sheet (m³) must be
      // converted here — the central derivation — rather than at each
      // consumer. The Kill Sheet's surface-line schedule row reads the rig
      // sheet directly through its own resolver, so it never sees this
      // converted value and cannot double-convert.
      const isMetric = p.units === "SI";
      const value = isMetric ? Number(toImperialValue(n, "bbl", "metric")) : n;
      return { value, confidence: isMetric ? "derived" : "exact",
        source: isMetric
          ? { ...tag, label: `${tag.label || "Rig Data Sheet"} · Card 4 · ${n} m³ converted to bbl` }
          : tag };
    }
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Well Profile derivers
// ---------------------------------------------------------------------------
function _deriveFromWell(field, ctx) {
  if (!ctx.wellId) return null;
  const w = getWellProfile(ctx.wellId);
  if (!w) return null;
  const tag = src("well", w.id, w.wellName);
  switch (field) {
    case "well.id":           return { value: w.id, confidence: "exact", source: tag };
    case "well.name":         return _isNonEmpty(w.wellName)  ? { value: w.wellName,  confidence: "exact", source: tag } : null;
    case "well.uwi":          return _isNonEmpty(w.uwi)       ? { value: w.uwi,       confidence: "exact", source: tag } : null;
    // Gated on the Classification section having been explicitly saved —
    // see _sectionConfirmed above.
    case "well.environment":  return (_isNonEmpty(w.environment) && _sectionConfirmed(w, "classification")) ? { value: w.environment, confidence: "exact", source: tag } : null;
    case "well.bopLocation":  return (_isNonEmpty(w.bopLocation) && _sectionConfirmed(w, "classification")) ? { value: w.bopLocation, confidence: "exact", source: tag } : null;
    case "well.wellType":     return (_isNonEmpty(w.wellType)    && _sectionConfirmed(w, "classification")) ? { value: w.wellType,    confidence: "exact", source: tag } : null;
    case "well.wellPurpose":  return (_isNonEmpty(w.wellPurpose) && _sectionConfirmed(w, "classification")) ? { value: w.wellPurpose, confidence: "exact", source: tag } : null;
    // Gated on the Status section having been explicitly saved.
    case "well.status":       return (_isNonEmpty(w.status) && _sectionConfirmed(w, "status")) ? { value: w.status, confidence: "exact", source: tag } : null;
    case "well.spudDate":     return _isNonEmpty(w.spudDate)  ? { value: w.spudDate,  confidence: "exact", source: tag } : null;
    case "well.waterDepthFt": return _isNonEmpty(w.waterDepthFt) ? { value: w.waterDepthFt, confidence: "exact", source: tag } : null;
    case "well.totalDepthPlannedFt": return _isNonEmpty(w.totalDepthPlannedFt) ? { value: w.totalDepthPlannedFt, confidence: "exact", source: tag } : null;
    case "well.totalDepthFinalFt":   return _isNonEmpty(w.totalDepthFinalFt)   ? { value: w.totalDepthFinalFt,   confidence: "exact", source: tag } : null;
    case "well.currentMdFt":  return _isNonEmpty(w.totalDepthCurrentFt) ? { value: w.totalDepthCurrentFt, confidence: "exact", source: tag } : null;
    case "well.currentTvdFt": return _isNonEmpty(w.totalDepthCurrentFt) ? { value: w.totalDepthCurrentFt, confidence: "derived", source: { ...tag, label: (tag.label || "") + " · MD≈TVD until survey wired" } } : null;
    case "well.casingShoeMdFt":
    case "well.casingShoeTvdFt": {
      const rows = w.children?.casingProgram || [];
      let max = 0; let pick = null;
      for (const r of rows) {
        const n = Number(r.bottomDepthFt);
        if (Number.isFinite(n) && n > max) { max = n; pick = r; }
      }
      if (!pick || max <= 0) return null;
      const lbl = `Casing row · ${pick.holeSection || pick.id || ""}`;
      const conf = field === "well.casingShoeMdFt" ? "derived" : "derived";
      return { value: max, confidence: conf, source: { ...tag, label: lbl } };
    }
    case "well.holeSection": {
      // Last casing row that is currently being drilled past — pick the
      // deepest row that has a label. The user may override via the session.
      const rows = w.children?.casingProgram || [];
      if (rows.length === 0) return null;
      const sorted = rows.slice().sort((a, b) => Number(b.bottomDepthFt || 0) - Number(a.bottomDepthFt || 0));
      const lbl = sorted[0].holeSection;
      if (!_isNonEmpty(lbl)) return null;
      return { value: lbl, confidence: "derived", source: tag };
    }
    case "well.holeSizeIn": {
      // Open-hole (drilled) diameter. Source precedence:
      //   (1) the Well Profile's MANUAL open-hole diameter override;
      //   (2) the bound BHA's bit size (RC-1 — session override first,
      //       then well.activeBhaId);
      //   (3) unknown — reported, never substituted.
      // A casing row's NOMINAL size is NOT a drilled diameter and is no
      // longer used here (per explicit operator instruction).
      const oh = resolveOpenHoleDiameterIn({ well: w, bha: null });
      if (oh.value != null) {
        return { value: oh.value, confidence: "exact",
          source: { ...tag, label: "Open-hole diameter (manual)" } };
      }
      if (oh.invalid) return null;
      const effBhaId = _effectiveBhaId(ctx);
      if (effBhaId) {
        const b = getBha(effBhaId);
        if (b && _isNonEmpty(b.bitSize)) {
          return { value: b.bitSize, confidence: "derived", source: src("bha", b.id, "Bit size") };
        }
      }
      return null;
    }
    // RCE-4.5 — Well geometry derivations. Calls buildWellState (the
    // unified geometry engine) and projects the snapshot fields.
    // RC-1 — wellState reads the EFFECTIVE BHA/DS ids (session override
    // first, well's active config second) so volumes resolve automatically
    // when the user has not opened SessionSwitcher.
    case "well.stringInternalVolBbl": {
      const st = buildWellState({ wellId: ctx.wellId,
        bhaId: _effectiveBhaId(ctx), drillStringId: _effectiveDrillStringId(ctx) });
      const v  = st.snapshot.stringInternalVolBbl;
      if (!_isNonEmpty(v)) return null;
      // A partial total is never presented as complete: when a component's
      // bore could not be resolved, say which one, in the label the
      // Engineering Snapshot already displays.
      const miss = st.snapshot.stringInternalVolMissing || [];
      const complete = st.snapshot.stringInternalVolComplete !== false;
      return { value: v, confidence: "derived",
        source: { ...tag, label: complete
          ? "Drill String + BHA"
          : `Drill String + BHA · INCOMPLETE — no internal bore for: ${miss.join("; ")}` } };
    }
    case "well.bitToShoeVolBbl": {
      const st = buildWellState({ wellId: ctx.wellId,
        bhaId: _effectiveBhaId(ctx), drillStringId: _effectiveDrillStringId(ctx) });
      const v  = st.snapshot.bitToShoeVolBbl;
      if (!_isNonEmpty(v)) return null;
      const issues = st.snapshot.annularVolIssues || [];
      return { value: v, confidence: "derived",
        source: { ...tag, label: issues.length === 0
          ? "Open-hole annulus"
          : `Open-hole annulus · INCOMPLETE — ${issues.join("; ")}` } };
    }
    case "well.shoeToSurfaceVolBbl": {
      const st = buildWellState({ wellId: ctx.wellId,
        bhaId: _effectiveBhaId(ctx), drillStringId: _effectiveDrillStringId(ctx) });
      const v  = st.snapshot.shoeToSurfaceVolBbl;
      if (!_isNonEmpty(v)) return null;
      const issues = st.snapshot.annularVolIssues || [];
      return { value: v, confidence: "derived",
        source: { ...tag, label: issues.length === 0
          ? "Cased annulus · shoe → rig floor"
          : `Cased annulus · shoe → rig floor · INCOMPLETE — ${issues.join("; ")}` } };
    }
    case "well.lotPressurePsi":
      return _isNonEmpty(w.lotPressurePsi) ? { value: w.lotPressurePsi, confidence: "exact", source: tag } : null;
    case "well.mwAtLotPpg":
      return _isNonEmpty(w.mwAtLotPpg) ? { value: w.mwAtLotPpg, confidence: "exact", source: tag } : null;
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// BHA derivers
// ---------------------------------------------------------------------------
function _deriveFromBha(field, ctx) {
  if (!ctx.bhaId) return null;
  const b = getBha(ctx.bhaId);
  if (!b) return null;
  const tag = src("bha", b.id, b.name || b.bhaNumber || "BHA");
  switch (field) {
    case "bha.id":       return { value: b.id, confidence: "exact", source: tag };
    case "bha.revision": return _isNonEmpty(b.revision) ? { value: b.revision, confidence: "exact", source: tag } : null;
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Drill String derivers
// ---------------------------------------------------------------------------
function _deriveFromDrillString(field, ctx) {
  if (!ctx.drillStringId) return null;
  const d = getDrillString(ctx.drillStringId);
  if (!d) return null;
  const tag = src("drillString", d.id, d.name || d.stringNumber || "Drill String");
  switch (field) {
    case "drillString.id":       return { value: d.id, confidence: "exact", source: tag };
    case "drillString.revision": return _isNonEmpty(d.revision) ? { value: d.revision, confidence: "exact", source: tag } : null;
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// Settings derivers (RCE-4.5) — engineering operating defaults.
// ---------------------------------------------------------------------------
function _deriveFromSettings(field /*, ctx */) {
  if (!field.startsWith("settings.")) return null;
  const defaults = getEngineeringDefaults();
  const key = field.slice("settings.".length);
  if (!(key in defaults)) return null;
  const value = defaults[key];
  if (!_isNonEmpty(value)) return null;
  return { value, confidence: "exact", source: src("settings", "engineeringDefaults", key) };
}

// ---------------------------------------------------------------------------
// Drilling Fluid Management derivers (RCE-3) — implemented in
// `lib/mud/derivers.js` to avoid a circular dependency with the resolver.
// ---------------------------------------------------------------------------
function _deriveFromMud(field, ctx) {
  try { return deriveMudField(field, ctx); }
  catch { return null; }
}

function safeList(fn) { try { return fn() || []; } catch { return []; } }

// ---------------------------------------------------------------------------
// Master deriver registry
// ---------------------------------------------------------------------------
const DERIVER_PIPELINE = [
  _deriveFromProject,
  _deriveFromRig,
  _deriveFromWell,
  _deriveFromBha,
  _deriveFromDrillString,
  _deriveFromMud,
  _deriveFromSettings,
];

/**
 * Walk every deriver and return EVERY candidate that publishes the field.
 * The resolver decides what to do with multi-source results (conflict vs.
 * confidence pick).
 */
export function deriveCandidates(field, ctx = {}) {
  const out = [];
  for (const fn of DERIVER_PIPELINE) {
    try {
      const r = fn(field, ctx);
      if (r != null) out.push(r);
    } catch { /* derivers must never throw — keep walking */ }
  }
  return out;
}
